import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import type { ComponentProps, ReactNode } from "react";
import { cn } from "../lib/cn";
import { Spinner } from "./spinner";

const buttonVariants = cva(
  [
    "transition-fg relative inline-flex w-fit shrink-0 cursor-pointer select-none items-center justify-center gap-x-1.5 overflow-hidden whitespace-nowrap rounded-md outline-none",
    "disabled:cursor-not-allowed disabled:bg-ui-bg-disabled disabled:text-ui-fg-disabled disabled:shadow-buttons-neutral",
    "[&_svg]:shrink-0",
  ],
  {
    variants: {
      variant: {
        primary:
          "bg-ui-button-inverted text-ui-contrast-fg-primary shadow-buttons-inverted hover:bg-ui-button-inverted-hover active:bg-ui-button-inverted-pressed focus-visible:shadow-buttons-inverted-focus",
        secondary:
          "bg-ui-button-neutral text-ui-fg-base shadow-buttons-neutral hover:bg-ui-button-neutral-hover active:bg-ui-button-neutral-pressed focus-visible:shadow-buttons-neutral-focus",
        transparent:
          "bg-ui-button-transparent text-ui-fg-base hover:bg-ui-button-transparent-hover active:bg-ui-button-transparent-pressed focus-visible:bg-ui-bg-base focus-visible:shadow-buttons-neutral-focus disabled:bg-transparent disabled:shadow-none",
        danger:
          "bg-ui-button-danger text-ui-fg-on-color shadow-buttons-danger hover:bg-ui-button-danger-hover active:bg-ui-button-danger-pressed focus-visible:shadow-buttons-danger-focus",
        accent:
          "bg-ui-bg-interactive text-ui-fg-on-color shadow-borders-interactive-with-shadow hover:brightness-110 active:brightness-95 focus-visible:shadow-borders-interactive-with-focus",
      },
      size: {
        small: "txt-compact-small-plus h-7 px-2",
        base: "txt-compact-small-plus h-8 px-3",
        large: "txt-compact-medium-plus h-10 px-4",
        xlarge: "txt-compact-large-plus h-12 rounded-lg px-5",
      },
      block: {
        true: "w-full",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "base",
    },
  },
);

export interface ButtonProps extends ComponentProps<"button">, VariantProps<typeof buttonVariants> {
  /** Render the child element (a router Link, an anchor) with button styling. */
  asChild?: boolean;
  /** Shows a spinner and blocks clicks while work is in flight. */
  isLoading?: boolean;
  children?: ReactNode;
}

export function Button({
  className,
  variant,
  size,
  block,
  asChild = false,
  isLoading = false,
  disabled,
  children,
  type,
  ...props
}: ButtonProps) {
  const classes = cn(buttonVariants({ variant, size, block }), className);

  if (asChild) {
    return (
      <Slot.Root className={classes} {...props}>
        {children}
      </Slot.Root>
    );
  }

  return (
    <button
      type={type ?? "button"}
      className={classes}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      {...props}
    >
      {isLoading ? (
        <>
          <span className="absolute inset-0 flex items-center justify-center">
            <Spinner />
          </span>
          <span className="invisible inline-flex items-center gap-x-1.5">{children}</span>
        </>
      ) : (
        children
      )}
    </button>
  );
}

const iconButtonVariants = cva(
  [
    "transition-fg inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md outline-none",
    "disabled:cursor-not-allowed disabled:bg-ui-bg-disabled disabled:text-ui-fg-disabled disabled:shadow-buttons-neutral",
  ],
  {
    variants: {
      variant: {
        primary:
          "bg-ui-button-neutral text-ui-fg-subtle shadow-buttons-neutral hover:bg-ui-button-neutral-hover active:bg-ui-button-neutral-pressed focus-visible:shadow-buttons-neutral-focus",
        transparent:
          "bg-ui-button-transparent text-ui-fg-muted hover:bg-ui-button-transparent-hover hover:text-ui-fg-subtle active:bg-ui-button-transparent-pressed focus-visible:bg-ui-bg-base focus-visible:shadow-buttons-neutral-focus disabled:bg-transparent disabled:shadow-none",
      },
      size: {
        "2xsmall": "size-5",
        xsmall: "size-6",
        small: "size-7",
        base: "size-8",
        large: "size-10",
      },
    },
    defaultVariants: { variant: "transparent", size: "small" },
  },
);

export interface IconButtonProps
  extends ComponentProps<"button">,
    VariantProps<typeof iconButtonVariants> {
  /** Icon buttons have no visible text, so a label is required for screen readers. */
  "aria-label": string;
  asChild?: boolean;
}

export function IconButton({
  className,
  variant,
  size,
  asChild = false,
  type,
  ...props
}: IconButtonProps) {
  const classes = cn(iconButtonVariants({ variant, size }), className);
  if (asChild) return <Slot.Root className={classes} {...props} />;
  return <button type={type ?? "button"} className={classes} {...props} />;
}

export { buttonVariants };
